/**
 * Portal Configuration Service
 * Detects domain and mode for dual-mode chat operation
 * 
 * Modes:
 * - Public (chat.realestate): Full registration flow
 * - Portal (aisearchportal.com): Pre-authenticated, no registration
 */

import axiosInstance from '../helpers/api';

export interface AgentProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  photo?: string;
  brokerage: {
    id: string;
    name: string;
    logo?: string;
  };
  license?: string;
}

export interface BuyerProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  preferences?: {
    budget?: { min: number; max: number };
    location?: string[];
    propertyType?: string[];
    features?: string[];
    beds?: number;
    baths?: number;
  };
}

export interface PortalConfig {
  mode: 'public' | 'portal';
  domain: string;
  agentSlug?: string;
  agent?: AgentProfile;
  buyer?: BuyerProfile;
  features?: {
    showRegistration: boolean;
    showEmailCapture: boolean;
    showAgentBranding: boolean;
  };
}

class PortalConfigService {
  private mode: 'public' | 'portal' = 'public';
  private config: PortalConfig | null = null;
  private agentSlug: string | null = null;
  
  constructor() {
    this.detectMode();
  }
  
  private detectMode() {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    
    // AISearchPortal domain detection
    if (hostname.includes('aisearchportal.com')) {
      this.mode = 'portal';
      this.extractAgentSlug();
      this.loadPortalConfig();
      return;
    }
    
    // Chat.realestate and localhost are public mode
    if (hostname.includes('chat.realestate') || 
        hostname === 'localhost' || 
        hostname.includes('127.0.0.1')) {
      this.mode = 'public';
      
      // Check if we're testing portal mode locally
      // e.g., localhost:3000/agent1
      if (pathname.startsWith('/agent') || pathname.includes('/portal/')) {
        this.mode = 'portal';
        this.extractAgentSlug();
        this.loadPortalConfig();
      }
      return;
    }
    
    // OfferNowAZ domain - public mode with special routing
    if (hostname.includes('offernowaz.com')) {
      this.mode = 'public';
      return;
    }
    
    // Default to public for unknown domains
    this.mode = 'public';
  }
  
  private extractAgentSlug() {
    const pathname = window.location.pathname;
    
    // Extract agent slug from path
    // e.g., /agent1 or /agent1/search
    const pathParts = pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      // First path segment is the agent slug
      this.agentSlug = pathParts[0];
    }
  }
  
  private async loadPortalConfig() {
    // For AISearchPortal with direct access, we don't need portal config
    // The buyer context is handled by the DirectAccess component
    if (window.location.hostname.includes('aisearchportal.com')) {
      console.log('🏠 [PortalConfig] AISearchPortal mode - using default config');
      this.config = {
        mode: 'portal',
        domain: window.location.hostname,
        agentSlug: this.agentSlug || undefined,
        features: {
          showRegistration: false,
          showEmailCapture: false,
          showAgentBranding: true
        }
      };
      return;
    }

    try {
      // Get invite token from URL params if available
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token') || this.getStoredInviteToken();
      
      // Load configuration
      const response = await axiosInstance.get('/portal/config', {
        params: { 
          token, 
          agentSlug: this.agentSlug,
          domain: window.location.hostname 
        },
        headers: {
          'X-Portal-Domain': window.location.hostname,
          'X-Agent-Slug': this.agentSlug || ''
        }
      });
      
      this.config = response.data;
      
      // Store token if provided
      if (token && response.data.buyer) {
        this.storeInviteToken(token);
      }
    } catch (error) {
      console.error('Failed to load portal configuration:', error);
      // Don't fallback to public mode - stay in portal mode but with limited config
      this.config = {
        mode: 'portal',
        domain: window.location.hostname,
        agentSlug: this.agentSlug || undefined,
        features: {
          showRegistration: false,
          showEmailCapture: false,
          showAgentBranding: true
        }
      };
    }
  }
  
  private getStoredInviteToken(): string | null {
    return localStorage.getItem('portal_invite_token');
  }
  
  private storeInviteToken(token: string) {
    localStorage.setItem('portal_invite_token', token);
  }
  
  public clearInviteToken() {
    localStorage.removeItem('portal_invite_token');
  }
  
  public isPortalMode(): boolean {
    return this.mode === 'portal';
  }
  
  public isPublicMode(): boolean {
    return this.mode === 'public';
  }
  
  public getAgentSlug(): string | null {
    return this.agentSlug;
  }
  
  public getAgentProfile(): AgentProfile | null {
    return this.config?.agent || null;
  }
  
  public getBuyerProfile(): BuyerProfile | null {
    return this.config?.buyer || null;
  }
  
  public getConfig(): PortalConfig | null {
    return this.config;
  }
  
  public shouldShowRegistration(): boolean {
    if (this.mode === 'public') return true;
    return this.config?.features?.showRegistration || false;
  }
  
  public shouldShowEmailCapture(): boolean {
    if (this.mode === 'public') return true;
    return this.config?.features?.showEmailCapture || false;
  }
  
  public shouldShowAgentBranding(): boolean {
    if (this.mode === 'portal') return true;
    return this.config?.features?.showAgentBranding || false;
  }
  
  // Refresh configuration (useful after login or token validation)
  public async refresh() {
    if (this.mode === 'portal') {
      await this.loadPortalConfig();
    }
  }
}

// Create singleton instance
const portalConfigService = new PortalConfigService();

// Export both the instance and class
export default portalConfigService;
export { PortalConfigService };